import { ICS2InformationExchangeMessages } from 'store/declarations/eu/ics2/ICS2InformationExchangeMessages';

/**
 * An inbound IE3Q02/IE3Q03 is persisted as one `Ics2CustomsRequest` with 1..999 child referrals.
 * Answering it means writing a draft per referral and then submitting a selection of them, which
 * the backend composes into an IE3R02 (Additional information response) or IE3R03 (HRCM screening
 * response).
 */
export enum Ics2ReferralType {
    RFI = 'RFI', // Request for information
    RFS = 'RFS', // Request for HRCM screening
    AMD = 'AMD', // Request to amend the ENS filing - answered by an amendment, or by IE3R02 declining it
}

export enum Ics2ReferralStatus {
    ACTION_REQUIRED = 'ACTION_REQUIRED',
    RESPONSE_IN_PROGRESS = 'RESPONSE_IN_PROGRESS',
    RESPONSE_QUEUED = 'RESPONSE_QUEUED',
    RESPONSE_BLOCKED = 'RESPONSE_BLOCKED',
    RESPONSE_REJECTED = 'RESPONSE_REJECTED',
    RESPONSE_SENT = 'RESPONSE_SENT',
    AMENDMENT_PENDING = 'AMENDMENT_PENDING',
    RESOLVED_BY_IE3R02 = 'RESOLVED_BY_IE3R02',
    RESOLVED_BY_IE3R03 = 'RESOLVED_BY_IE3R03',
    RESOLVED_BY_AMENDMENT = 'RESOLVED_BY_AMENDMENT',
}

export enum Ics2CustomsRequestStatus {
    ACTION_REQUIRED = 'ACTION_REQUIRED',
    RESPONSE_IN_PROGRESS = 'RESPONSE_IN_PROGRESS',
    RESPONSE_QUEUED = 'RESPONSE_QUEUED',
    RESPONSE_SENT = 'RESPONSE_SENT',
    RESOLVED = 'RESOLVED',
}

export interface Ics2TransportDocument {
    documentNumber?: string;
    type?: string;
}

export interface Ics2AdditionalInformation {
    code?: string;
    text?: string;
    type?: string;
}

export interface Ics2AuthorizedPerson {
    name?: string;
    identificationNumber?: string;
    typeOfPerson?: string;
}

export interface Ics2FacilityAddress {
    city?: string;
    country?: string;
    subDivision?: string;
    street?: string;
    postCode?: string;
    streetAdditionalLine?: string;
    number?: string;
    poBox?: string;
}

/**
 * The response draft stored against a single referral. RFI/AMD referrals use only
 * `additionalInformation` and `acceptedAmendmentRequestId`; the screening fields are rejected
 * with a 400 on anything other than an RFS referral.
 */
export interface Ics2ReferralDraft {
    acceptedAmendmentRequestId?: string;
    additionalInformation?: Ics2AdditionalInformation[];
    result?: string;
    screeningMethods?: string[];
    authorizedPerson?: Ics2AuthorizedPerson;
    facilityAddress?: Ics2FacilityAddress;
    transportDocument?: Ics2TransportDocument;
}

export interface Ics2ReferralRequestDetails {
    referralRequestReference?: string;
    requestType?: string;
    transportDocumentHouse?: Ics2TransportDocument;
    transportDocumentMaster?: Ics2TransportDocument;
    supportingDocuments?: Array<{ referenceNumber?: string; type?: string }>;
    additionalInformation?: Ics2AdditionalInformation[];
    pointer?: { messageElementPath?: string };
    recommendedHRCMScreeningMethod?: Array<{ method?: string }>;
}

export interface Ics2Referral {
    id: string;
    referralRequestReference: string;
    requestType: Ics2ReferralType;
    status: Ics2ReferralStatus;
    sequenceNumber: number;
    requestDetails?: Ics2ReferralRequestDetails;
    draftVersion: number;
    responseDraft?: Ics2ReferralDraft;
    draftUpdatedAt?: string;
    draftUpdatedBy?: string;
}

export interface Ics2CustomsRequest {
    id: string;
    notificationId: string;
    transactionId?: string;
    messageType: ICS2InformationExchangeMessages.IE3Q02 | ICS2InformationExchangeMessages.IE3Q03;
    status: Ics2CustomsRequestStatus;
    mrn?: string;
    responsibleMemberStateCountry?: string;
    documentIssueDate?: string;
    receivedAt?: string;
    requestPayload?: Ics2RequestPayload;
    responseDraftVersion?: number;
    responseDraft?: unknown;
    responseDraftUpdatedAt?: string;
    responseDraftUpdatedBy?: string;
    referrals: Ics2Referral[];
}

export interface Ics2RequestPayload {
    // Note: To be tested once we have actual data (currently the BE seeds mock) and remove one of the mrn fields from the payload
    mrn?: string;
    MRN?: string;
    documentIssueDate?: { dateTime?: string; DateTime?: string };
    responsibleMemberState?: { country?: string };
    declarant?: { identificationNumber?: string };
    representative?: { identificationNumber?: string };
    transportDocument?: Ics2TransportDocument;
    referralRequestDetails?: Ics2ReferralRequestDetails[];
}

export interface Ics2ReferralDraftRequest {
    version: number;
    data: Ics2ReferralDraft;
}

export interface Ics2ReferralSubmitRequest {
    draftVersions: Record<string, number>;
    documentVersions: Record<string, number>;
    requestDraftVersions?: Record<string, number>;
}

export enum Ics2SubmissionStatus {
    QUEUED = 'QUEUED',
    DISPATCHING = 'DISPATCHING',
    BLOCKED = 'BLOCKED',
    SENT = 'SENT',
    REJECTED = 'REJECTED',
}

export interface Ics2ReferralSubmission {
    id: string;
    declarationId?: string;
    requestNotificationId?: string;
    status: Ics2SubmissionStatus;
    transactionId?: string;
    createdAt?: string;
    createdBy?: string;
    nextAttemptAt?: string;
    attempts?: number;
    lastError?: string;
    referralVersions?: string;
}

export enum Ics2AmendmentStatus {
    QUEUED = 'QUEUED',
    SUBMITTING = 'SUBMITTING',
    BLOCKED = 'BLOCKED',
    PENDING = 'PENDING',
    ACCEPTED = 'ACCEPTED',
    REJECTED = 'REJECTED',
}

export interface Ics2AmendmentSummary {
    requestId: string;
    referralId: string;
    status: Ics2AmendmentStatus;
    transactionId?: string;
    createdAt?: string;
}
